import React, { useEffect, useMemo, useState } from 'react'
import { SkeletonHabitList } from '../../Components/Skeleton/Skeleton'
import type { Habit, HabitIcon } from '../../Models/Habit'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
  createHabit,
  deleteHabit,
  fetchHabits,
  selectActiveHabits,
  selectArchivedHabits,
  selectHabitsStatus,
  updateHabit,
} from '../../redux/habits'
import styles from './Habits.module.css'

const ICONS: HabitIcon[] = ['sun', 'workout', 'book']

const nextIcon = (i: HabitIcon): HabitIcon => {
  const idx = ICONS.indexOf(i)
  return ICONS[(idx + 1) % ICONS.length]
}

const SunGlyph = ({ accent }: { accent?: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle
      cx="12"
      cy="12"
      r="4"
      stroke={accent ? '#0ea5e9' : '#4b5563'}
      strokeWidth="1.75"
    />
    <path
      d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
      stroke={accent ? '#0ea5e9' : '#4b5563'}
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
)

const WorkoutGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M6.5 10.5h-2a1 1 0 00-1 1v1a1 1 0 001 1h2M17.5 10.5h2a1 1 0 011 1v1a1 1 0 01-1 1h-2M8 12h8M9 8l-2.5 4M15 8l2.5 4M10.5 15.5h3"
      stroke="#4b5563"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const BookGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 5.5A2.5 2.5 0 016.5 3H20v16H6.5A2.5 2.5 0 014 16.5v-11zM4 16.5A2.5 2.5 0 016.5 19H20"
      stroke="#4b5563"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const HabitGlyph = ({ icon }: { icon: HabitIcon }) => {
  switch (icon) {
    case 'sun':
      return <SunGlyph accent />
    case 'workout':
      return <WorkoutGlyph />
    case 'book':
      return <BookGlyph />
    default:
      return <SunGlyph />
  }
}

const ArrowRightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CaretRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M9 3h6M4 7h16M10 11v6M14 11v6M6 7l1 14h10l1-14"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CycleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M21 12a9 9 0 11-3-7.1M21 3v6h-6"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ArchiveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 7h16M6 7V5a1 1 0 011-1h10a1 1 0 011 1v2M6 7v12a1 1 0 001 1h10a1 1 0 001-1V7M10 11h4"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

type HabitRowProps = {
  habit: Habit
  archived: boolean
  onCycleIcon: (id: string, icon: HabitIcon) => void
  onArchive: (id: string) => void
  onRestore: (id: string) => void
  onDelete: (id: string, title: string) => void
  editingId: string | null
  editingValue: string
  onStartEdit: (h: Habit) => void
  onChangeEdit: (v: string) => void
  onCommitEdit: () => void
  onCancelEdit: () => void
}

const HabitRow = ({
  habit,
  archived,
  onCycleIcon,
  onArchive,
  onRestore,
  onDelete,
  editingId,
  editingValue,
  onStartEdit,
  onChangeEdit,
  onCommitEdit,
  onCancelEdit,
}: HabitRowProps) => {
  const isEditing = editingId === habit.id

  return (
    <li className={styles.row}>
      <div className={styles.iconBox} aria-hidden>
        <HabitGlyph icon={habit.icon} />
      </div>
      <div className={styles.rowBody}>
        {isEditing ? (
          <input
            type="text"
            className={styles.rowEditInput}
            value={editingValue}
            autoFocus
            onChange={(e) => onChangeEdit(e.target.value)}
            onBlur={onCommitEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                onCommitEdit()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                onCancelEdit()
              }
            }}
            aria-label={`Edit habit title: ${habit.title}`}
          />
        ) : (
          <button
            type="button"
            className={styles.rowTitleBtn}
            onClick={() => onStartEdit(habit)}
            title="Edit title"
          >
            {habit.title}
          </button>
        )}
        {habit.category ? <span className={styles.category}>{habit.category}</span> : null}
      </div>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.iconBtn}
          title="Change icon"
          aria-label={`Change icon for ${habit.title}`}
          onClick={() => onCycleIcon(habit.id, habit.icon)}
        >
          <CycleIcon />
        </button>
        {archived ? (
          <button type="button" className={styles.textBtn} onClick={() => onRestore(habit.id)}>
            Restore
          </button>
        ) : (
          <button
            type="button"
            className={styles.iconBtn}
            title="Archive"
            aria-label={`Archive ${habit.title}`}
            onClick={() => onArchive(habit.id)}
          >
            <ArchiveIcon />
          </button>
        )}
        <button
          type="button"
          className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
          title="Delete"
          aria-label={`Delete ${habit.title}`}
          onClick={() => onDelete(habit.id, habit.title)}
        >
          <TrashIcon />
        </button>
      </div>
    </li>
  )
}

const Habits = () => {
  const dispatch = useAppDispatch()
  const active = useAppSelector(selectActiveHabits)
  const archived = useAppSelector(selectArchivedHabits)
  const habitsStatus = useAppSelector(selectHabitsStatus)
  const error = useAppSelector((s) => s.habits.error)

  const [draftTitle, setDraftTitle] = useState('')
  const [activeOpen, setActiveOpen] = useState(true)
  const [archivedOpen, setArchivedOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingValue, setEditingValue] = useState('')

  useEffect(() => {
    void dispatch(fetchHabits())
  }, [dispatch])

  const metaLabel = useMemo(() => {
    const a = active.length
    const ar = archived.length
    const parts: string[] = []
    parts.push(a === 1 ? '1 active' : `${a} active`)
    parts.push(ar === 1 ? '1 archived' : `${ar} archived`)
    return parts.join(' · ')
  }, [active.length, archived.length])

  const submit = () => {
    const t = draftTitle.trim()
    if (!t) return
    void dispatch(createHabit({ title: t }))
    setDraftTitle('')
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  const cycleIcon = (id: string, icon: HabitIcon) => {
    void dispatch(updateHabit({ id, patch: { icon: nextIcon(icon) } }))
  }

  const onArchive = (id: string) => {
    void dispatch(
      updateHabit({ id, patch: { archivedAt: new Date().toISOString() } })
    )
  }

  const onRestore = (id: string) => {
    void dispatch(updateHabit({ id, patch: { archivedAt: null } }))
  }

  const onDelete = (id: string, title: string) => {
    if (!window.confirm(`Delete “${title}”? This cannot be undone.`)) return
    if (editingId === id) {
      setEditingId(null)
      setEditingValue('')
    }
    void dispatch(deleteHabit(id))
  }

  const startEdit = (h: Habit) => {
    setEditingId(h.id)
    setEditingValue(h.title)
  }

  const commitEdit = () => {
    if (!editingId) return
    const trimmed = editingValue.trim()
    const original = [...active, ...archived].find((h) => h.id === editingId)
    if (!original) {
      setEditingId(null)
      setEditingValue('')
      return
    }
    if (!trimmed || trimmed === original.title) {
      setEditingId(null)
      setEditingValue('')
      return
    }
    void dispatch(updateHabit({ id: editingId, patch: { title: trimmed } }))
    setEditingId(null)
    setEditingValue('')
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditingValue('')
  }

  const renderSection = (
    title: string,
    habits: Habit[],
    open: boolean,
    onToggle: () => void,
    isArchived: boolean,
    emptyText: string
  ) => (
    <div className={styles.section}>
      <button
        type="button"
        className={styles.sectionHead}
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className={`${styles.sectionChevron} ${open ? styles.sectionChevronOpen : ''}`}>
          <CaretRight />
        </span>
        <span className={styles.sectionTitle}>{title}</span>
        <span className={styles.sectionCount}>{habits.length}</span>
      </button>
      {open ? (
        habits.length === 0 ? (
          <p className={styles.sectionEmpty}>{emptyText}</p>
        ) : (
          <ul className={styles.list}>
            {habits.map((h) => (
              <HabitRow
                key={h.id}
                habit={h}
                archived={isArchived}
                onCycleIcon={cycleIcon}
                onArchive={onArchive}
                onRestore={onRestore}
                onDelete={onDelete}
                editingId={editingId}
                editingValue={editingValue}
                onStartEdit={startEdit}
                onChangeEdit={setEditingValue}
                onCommitEdit={commitEdit}
                onCancelEdit={cancelEdit}
              />
            ))}
          </ul>
        )
      ) : null}
    </div>
  )

  return (
    <div className={styles.container}>
      <section className={styles.hero} aria-labelledby="habits-page-heading">
        <span className={styles.kicker}>Plan your routine</span>
        <h1 id="habits-page-heading" className={styles.title}>
          Shape your habits.
        </h1>
        <p id="habit-add-hint" className={styles.captureHint}>
          Enter a <strong>name</strong> for your habit, then use <strong>Add</strong> or press Enter.
        </p>
        <div className={styles.captureCard} aria-describedby="habit-add-hint">
          <input
            type="text"
            className={styles.captureInput}
            placeholder="New habit name…"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Habit name (required)"
            title="The habit title shown in your list and weekly tracker"
          />
          <button
            type="button"
            className={styles.submitBtn}
            onClick={submit}
            disabled={!draftTitle.trim()}
            aria-label="Add habit to your list"
            title="Save this habit (requires a name)"
          >
            <ArrowRightIcon />
          </button>
        </div>
      </section>

      {error ? <p className={styles.errorBanner}>{error}</p> : null}

      <section className={styles.inbox} aria-label="Habit list">
        <p className={styles.meta}>{metaLabel}</p>
        {habitsStatus === 'loading' && active.length === 0 && archived.length === 0 ? (
          <SkeletonHabitList count={5} />
        ) : (
          <>
            {renderSection(
              'Active habits',
              active,
              activeOpen,
              () => setActiveOpen((v) => !v),
              false,
              'No active habits — add one above.'
            )}
            {renderSection(
              'Archived',
              archived,
              archivedOpen,
              () => setArchivedOpen((v) => !v),
              true,
              'No archived habits.'
            )}
          </>
        )}
      </section>
    </div>
  )
}

export default Habits
